<template>
  <div>
    <div
      class="flex items-center"
      :class="{
        'justify-between': $route.name !== 'SharedWithMe' || selectedPresentations.length !== 0,
        'justify-end': $route.name === 'SharedWithMe'
      }"
    >
      <div
        class="flex items-center"
        v-if="selectedPresentations.length !== 0"
      >
        <aha-antd-button
          v-aha-emit-action
          name="my-presentations-delete"
          :disabled="!selectedPresentations || !selectedPresentations.length"
          class="mr-4"
          size="lg"
          variant="danger"
          icon-name="delete"
          @click="isModalDeletePresentationsShowing = true"
        >
          <span v-if="canUseTrashBin">{{ $i18nPresenter.t("Move to trash") }}</span>
          <span v-else>{{ $i18nPresenter.t("Delete") }}</span>
        </aha-antd-button>

        <aha-antd-button
          v-aha-emit-action
          name="my-presentations-move-to-folder"
          :disabled="!selectedPresentations || !selectedPresentations.length"
          class="mr-4"
          size="lg"
          icon-name="move"
          variant="secondary"
          @click="openMovePresentationModal"
          v-if="isShowMoveTo"
        >
          <span>{{ $i18nPresenter.t("Move to") }}</span>
        </aha-antd-button>

        <span class="text-base-90">
          <span
            v-html="$i18nPresenter.t('xselectedPresentations',{
              length: selectedPresentations.length
            })"
          />
        </span>
      </div>

      <div
        v-if="selectedPresentations.length === 0 && $route.name !== 'SharedWithMe'"
        class="buttons-container"
      >
        <div>
          <div v-if="canUseNewButtons">
            <!-- desktop new presentation button -->
            <aha-antd-button
              v-if="isShowNewPresentationButton"
              v-aha-emit-action
              name="my-presentations-new-presentation-desktop"
              class="__new-presentation-btn--desktop"
              :class="{'disabled': isButtonNewPresentationDisabled}"
              :disabled="isButtonNewPresentationDisabled"
              icon-name="add"
              size="lg"
              @click="onClickNewPresentation"
              v-tooltip="isButtonNewPresentationDisabled ? buttonNewPresentationDisabledTooltip : null"
            >
              <span>{{ $i18nPresenter.t("New Presentation") }}</span>
            </aha-antd-button>

            <!-- mobile new presentation button -->
            <aha-antd-button
              v-if="isShowNewPresentationButton"
              v-aha-emit-action
              name="my-presentations-new-presentation-mobile"
              class="__new-presentation-btn--mobile"
              :class="{'disabled': isButtonNewPresentationDisabled}"
              size="lg"
              icon-name="add"
              @click="handleNewClick"
              :disabled="isButtonNewPresentationDisabled"
              v-tooltip="isButtonNewPresentationDisabled ? buttonNewPresentationDisabledTooltip : null"
            >
              <span>{{ $i18nPresenter.t("New Presentation") }}</span>
            </aha-antd-button>
          </div>
          <!-- deprecated -->
          <div v-else>
            <!-- desktop new presentation button -->
            <aha-button
              v-if="isShowNewPresentationButton"
              class="md:inline-flex hidden mr-3"
              :class="{'disabled': isButtonNewPresentationDisabled}"
              :disabled="isButtonNewPresentationDisabled"
              size="xl"
              @click="onClickNewPresentation"
              v-tooltip="isButtonNewPresentationDisabled ? buttonNewPresentationDisabledTooltip : null"
            >
              <aha-icon
                name="add"
                width="16"
                height="16"
                viewBox="0 0 24 24"
              />
              <span>{{ $i18nPresenter.t("New Presentation") }}</span>
            </aha-button>

            <!-- mobile new presentation button -->
            <aha-button
              v-if="isShowNewPresentationButton"
              class="md:hidden inline mr-3"
              :class="{'disabled': isButtonNewPresentationDisabled}"
              :disabled="isButtonNewPresentationDisabled"
              size="xl"
              @click="handleNewClick"
              v-tooltip="isButtonNewPresentationDisabled ? buttonNewPresentationDisabledTooltip : null"
            >
              <aha-icon
                name="add"
                width="16"
                height="16"
                viewBox="0 0 24 24"
              />
              <span>{{ $i18nPresenter.t("New Presentation") }}</span>
            </aha-button>
          </div>

          <aha-context-menu ref="context-menu-new">
            <aha-context-menu-header>
              {{ $i18nPresenter.t("Start a new presentation") }}
            </aha-context-menu-header>
            <ul>
              <li
                v-if="workingMode === 'personal'"
                class="block context-menu-item md:hidden"
                @click="handleClickChooseTemplate"
              >
                <aha-icon
                  class="text-purple-50"
                  height="20"
                  width="20"
                  viewBox="0 0 20 20"
                  name="template"
                />
                <span>{{ $i18nPresenter.t("From a Template") }}</span>
              </li>
              <li
                class="context-menu-item"
                @click="onClickNewPresentation"
              >
                <aha-icon
                  class="text-brand-a100"
                  height="20"
                  width="20"
                  viewBox="0 0 24 24"
                  name="file"
                />
                <span class="inline md:hidden">{{ $i18nPresenter.t("From Scratch") }}</span>
                <span class="hidden md:inline">{{ $i18nPresenter.t("New Presentation") }}</span>
              </li>
            </ul>
          </aha-context-menu>
        </div>
        <aha-antd-button
          v-if="isShowNewFolderButton"
          v-aha-emit-action
          name="my-presentations-new-folder"
          class="__new-folder-button"
          variant="secondary"
          icon-name="newfolder"
          icon-view-box="0 0 25 25"
          size="lg"
          @click="handleClickCreateNew('folder')"
        >
          <span>{{ $i18nPresenter.t("New Folder") }}</span>
        </aha-antd-button>
        <aha-antd-button
          v-if="isShowImportButton"
          v-aha-emit-action
          name="my-presentations-import"
          class="__import-button"
          variant="secondary"
          size="lg"
          icon-name="import"
          :submitting="isImportingSlides"
          @click="openImportModal()"
        >
          <span>
            {{ $i18nPresenter.t("Import") }}
          </span>
        </aha-antd-button>
      </div>

      <sort-by
        v-if="showSortBy"
        class="hidden md:inline-block"
      />
    </div>

    <modal-create-new-folder
      v-if="isModalCreateNewFolderShowing"
      @hide="handleHideModal('create-new-folder')"
    />
    <modal-create-new-team-folder
      v-if="isModalCreateNewTeamFolderShowing"
      @hide="handleHideModal('create-new-team-folder')"
    />
    <modal-import
      v-if="$store.state.openImportModal"
    />

    <modal-move-presentation
      v-if="isModalMovePresentationShowing"
      :selectedPresentations="selectedPresentations"
      @hide="handleHideModal('move-presentation')"
      @handleShowAccessPermission="handleShowAccessPermission"
      :folders="folders"
      :destinatedFolderId="destinatedFolderId"
      @updateDestinatedFolderId="updateDestinatedFolderId"
    />
    <modal-move-team-presentation
      v-if="isModalMoveTeamPresentationShowing"
      :selectedPresentations="selectedPresentations"
      @hide="handleHideModal('move-team-presentation')"
    />
    <modal-delete-presentations
      v-if="isModalDeletePresentationsShowing"
      :deleted-presentations="selectedPresentations"
      @hide="isModalDeletePresentationsShowing = false"
    />

    <modal-access-permission
      :folders="folders"
      v-if="isModalAccessPermissionShowing"
      :destinatedFolderId="destinatedFolderId"
      @hide="isModalAccessPermissionShowing = false"
      :selectedPresentations="selectedPresentations"
      @handleMovePresn="handleMovePresn"
      :isMovingPresn="isMovingPresn"
    />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'

// utils
import Utils from '@/util'
import EventBus from '@/eventBus'

// api
// import PresentationApi from '@/api/presentation'
import FolderApi from '@/api/folder'
import PresentationApi from '@/api/presentation'

// constants
import constants from '@/constant'
import configs from '@/config'

// modals
import ModalCreateNewFolder from '@/components-v2/modals/CreateNewFolder'
import ModalCreateNewTeamFolder from '@/components-v2/modals/CreateNewTeamFolder'
import ModalImport from '@/components-v2/modals/Import.vue'
import ModalMovePresentation from '../modals/MovePresentations.vue'
import ModalMoveTeamPresentation from '../modals/MoveTeamPresentations.vue'
import ModalDeletePresentations from '../modals/DeletePresentations.vue'
import ModalAccessPermission from '../modals/AccessPermission.vue'

// components
import SortBy from './SortBy.vue'
import CreateNewPresentationMixin from '@/mixins/presentation/CreateNewPresentationMixin'

const { MAX_FOLDER_DEPTH, MAX_PRESENTATION_IN_FOLDER } = configs

export default {
  components: {
    SortBy,
    ModalCreateNewFolder,
    ModalCreateNewTeamFolder,
    ModalDeletePresentations,
    ModalImport,
    ModalMovePresentation,
    ModalMoveTeamPresentation,
    ModalAccessPermission,
  },
  mixins: [CreateNewPresentationMixin],
  props: {
    isHideNewFolder: {
      type: Boolean,
      default: false,
    },
    isHideSortBy: {
      type: Boolean,
      default: false,
    },
    isFromSearchingNoDataComponent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      newPresentationTrigger: null,
      isModalCreateNewFolderShowing: false,
      isModalCreateNewTeamFolderShowing: false,
      isModalImportShowing: false,
      isModalMovePresentationShowing: false,
      isModalMoveTeamPresentationShowing: false,
      isModalDeletePresentationsShowing: false,
      isModalAccessPermissionShowing: false,
      permissions: constants.permissions,
      selectedPresentationIds: [],
      destinatedFolderId: null,
      folders: [],
      isMovingPresn: false,
    }
  },
  computed: {
    ...mapState({
      selectedPresentations: (state) => {
        // const isTeamWorkSpace = state.workingMode === constants.workspace.TEAM
        // const isTeamAdminOrOwner = state.currentUser.accountRole === constants.workspace.ADMIN ||
        //   state.currentUser.accountRole === constants.workspace.OWNER
        return state.presentations.filter((presentation) => presentation.selected)
      },
      importStatus: (state) => state.importStatus,
      presentations: (state) => state.presentations,
      isImportingSlides: (state) => state.importStatus === 2 || state.importStatus === 3,
      workingMode: (state) => state.workingMode,
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      currentFolderDepth: (state) => state.currentFolderDepth,
      currentFolderPermission: (state) => state.currentFolderPermission,
      selectedFolderId: (state) => state.selections.selectedFolderId,
      currentFolder: (state) => state.currentFolder,
      listFolder: (state) => state.folders,
    }),
    ...mapGetters('featureToggles', ['canUseTrashBin', 'canUseNewButtons']),
    presentationSelected() {
      return this.presentations.some((presentation) => presentation.selected)
    },
    isShowNewPresentationButton() {
      if (this.isRoutePresentation) return true
      return this.checkPermissionFolderItem(this.permissions.CREATE_PRESENTATION)
    },
    isShowNewFolderButton() {
      if (this.isFolderDetailAndMaxFolderDepth) return false
      if (this.isRoutePresentation) return true
      return this.checkPermissionFolderItem(this.permissions.CREATE_FOLDER)
    },
    isShowImportButton() {
      if (this.isRoutePresentation) return true
      return this.checkPermissionFolderItem(this.permissions.CREATE_PRESENTATION)
    },
    isFolderDetailAndMaxFolderDepth() {
      return this.isRouteFolderDetail && this.currentFolderDepth >= this.parseIntMaxFolderDepth
    },
    isButtonNewPresentationDisabled() {
      return this.presentations && this.presentations.length === this.parseIntMaxPresentationInFolder
        && this.currentFolderDepth > 0
    },
    buttonNewPresentationDisabledTooltip() {
      return `Maximum presentations reached (${this.parseIntMaxPresentationInFolder}/${this.parseIntMaxPresentationInFolder})`
    },
    isRouteFolderDetail() {
      return this.$route.name === 'FolderDetail'
    },
    isRouteTeamFolderDetail() {
      return this.$route.name === 'TeamFolderDetail'
    },
    isRouteTeam() {
      return this.$route.name === 'Team'
    },
    isRouteShareFolderDetail() {
      return this.$route.name === 'ShareFolderDetail'
    },
    isRouteSharedWithMe() {
      return this.$route.name === 'SharedWithMe'
    },
    isRoutePresentation() {
      return this.$route.name === 'Presentations'
    },
    isShowMoveTo() {
      return !this.isRouteShareFolderDetail && !this.isRouteSharedWithMe
    },
    showSortBy() {
      if (this.presentations.length === 0 && this.listFolder.length === 0) return false
      return true
    },
    parseIntMaxFolderDepth() {
      return parseInt(MAX_FOLDER_DEPTH, 10)
    },
    parseIntMaxPresentationInFolder() {
      return parseInt(MAX_PRESENTATION_IN_FOLDER, 10)
    },
  },
  mounted() {
    EventBus.$on('my-presentations:create-new-presentation', ({ trigger }) => {
      this.newPresentationTrigger = trigger
      this.handleClickCreateNew('file')
    })
  },
  destroyed() {
    EventBus.$off('my-presentations:create-new-presentation')
  },
  methods: {
    ...mapActions(['setPresentationsAction']),
    handleNewClick() {
      this.$refs['context-menu-new'].show()
    },
    handleClickCreateNew(createNewSelectionIcon) {
      this.openCreateNewModal(createNewSelectionIcon)
    },
    openCreateNewModal(createNewSelectionIcon) {
      if (createNewSelectionIcon === 'file') {
        this.createNewPresentation(this.newPresentationTrigger)
      } else if (createNewSelectionIcon === 'folder') {
        if (this.workingMode === constants.workspace.PERSONAL) {
          this.isModalCreateNewFolderShowing = true
        }
        if (this.workingMode === constants.workspace.TEAM) {
          this.isModalCreateNewTeamFolderShowing = true
        }
        Utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'New > Create New Folder > Open modal', null, null, {
          isTeamFolder: this.workingMode === constants.workspace.TEAM,
          role: this.currentUser.accountRole || null,
          teamId: this.currentAccount.id || null,
          parentFolderId: this.selectedFolderId,
          folderDepth: this.currentFolderDepth || 0,
        })
      }
    },
    openImportModal() {
      this.$store.state.openImportModal = true
      if (this.isFromSearchingNoDataComponent) {
        Utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'Import > Open modal', 'WhenSearching')
      } else {
        Utils.mixpanelTracking(
          constants.eventCategories.PRESENTATION_LIST,
          'Import > Open modal',
        )
      }
    },
    handleHideModal(type) {
      if (type === 'create-new-folder') {
        this.isModalCreateNewFolderShowing = false
        Utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'New > Create New Folder > Close modal', null, null, {
          isTeamFolder: this.workingMode === constants.workspace.TEAM,
          role: this.currentUser.accountRole || null,
          teamId: this.currentAccount.id || null,
          parentFolderId: this.selectedFolderId,
          folderDepth: this.currentFolderDepth || 0,
        })
      } else if (type === 'create-new-team-folder') {
        this.isModalCreateNewTeamFolderShowing = false
        this.trackEvent('')
      } else if (type === 'create-new-presentation') {
        // this.isModalCreateNewPresentationShowing = false
        // this.trackEvent('New > Create New Presentation > Close modal')
      } else if (type === 'move-presentation') {
        this.isModalMovePresentationShowing = false
        this.trackEvent('Move > Move presentations to folder > Close modal')
      } else if (type === 'move-team-presentation') {
        this.isModalMoveTeamPresentationShowing = false
      }
    },
    trackEvent(label) {
      Utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_LIST,
        label,
      )
    },
    handleCloseButtonClick() {
      const newPresentations = this.presentations.map((presentation) => {
        return { ...presentation, selected: false }
      })

      this.setPresentationsAction(newPresentations)
    },
    handleClickChooseTemplate() {
      this.$router.push('/apps/template-library?from=mobileMenu')
    },
    onClickNewPresentation() {
      this.newPresentationTrigger = 'New dropdown menu'
      this.openCreateNewModal('file')
    },
    async openMovePresentationModal() {
      if (this.workingMode === constants.workspace.PERSONAL) {
        this.isModalMovePresentationShowing = true
      } else if (this.workingMode === constants.workspace.TEAM) {
        this.isModalMoveTeamPresentationShowing = true
      }
      Utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'Presentation List > Move presentation > Open select the destination modal', null, null, {
        role: this.$store.state.currentUser.accountRole || null,
        teamId: this.$store.state.currentAccount.id || null,
      })
      if (this.selectedPresentations.length) {
        this.selectedPresentationIds = this.selectedPresentations.map((presn) => presn.id)
      }
      this.loading = true
      await this.getAndSetFolders()
    },
    async getAndSetFolders() {
      const folderTree = await FolderApi.getFullFolderTree()
      const flatFolderTree = this._flattenFolderTree(folderTree)

      flatFolderTree.unshift({ folderId: null, folderName: this.$i18nPresenter.t('myPresentations') })
      this.folders = flatFolderTree.map((f) => {
        return {
          ...f,
          disabled: this.selectedPresentations.some((p) => (p.folderId === f.folderId)
            || (f.countPresentation === this.parseIntMaxPresentationInFolder && f.depth && f.depth > 0)),
          folderName: this.selectedPresentations.some((p) => p.folderId === f.folderId) ? `${f.folderName} (current)` : f.folderName,
        }
      })
      const enabled = this.folders.filter((f) => !f.disabled)
      this.destinatedFolderId = enabled && enabled[0] && enabled[0].folderId
    },
    _flattenFolderTree(folderTree) {
      return folderTree.reduce((acc, {
        folderId, folderName, parentFolderId, depth, folders, countPresentation, folderShareId, folderInviteId,
      }) => {
        acc.push({
          folderId, folderName, parentFolderId, depth, countPresentation, folderShareId, folderInviteId,
        })
        if (folders && folders.length > 0) {
          acc = acc.concat(this._flattenFolderTree(folders))
        }
        return acc
      }, [])
    },
    handleShowAccessPermission() {
      const folderMoveInTo = this.folders.filter((folder) => folder.folderId === this.destinatedFolderId)[0]
      if (folderMoveInTo.folderShareId || folderMoveInTo.folderInviteId) {
        this.isModalAccessPermissionShowing = true
      } else {
        this.handleMovePresn()
      }
    },
    updateDestinatedFolderId(value) {
      this.destinatedFolderId = value
    },
    async handleMovePresn() {
      this.isMovingPresn = true
      // if (!this.destinatedFolderId) {
      //   utils.showToastError(this.$i18nPresenter.t('Move presentation(s) failed: No folder selected'))
      //   this.isMovingPresn = false
      //   return
      // }
      try {
        const data = {
          presentationIds: this.selectedPresentationIds,
          presentation: { folderId: this.destinatedFolderId },
        }
        await PresentationApi.patchPresentations(data)
        this.handleAfterCallAPIMovePresn()
      } catch (error) {
        Sentry.captureException(error)
        this.isMovingPresn = false
        Utils.showToastError(this.$i18nPresenter.t('Move presentations failed'))
        this.trackEvent(
          'Presentation List > Move Presentation > Failed',
          error.body ? error.body.message : '',
        )
      }
    },
    handleAfterCallAPIMovePresn() {
      this.isMovingPresn = false

      this.handleShowToastSuccess(this.selectedPresentations)
      this.trackEvent('Presentation List > Move Presentation > Succeeded')
      this.isModalAccessPermissionShowing = false
    },
    handleShowToastSuccess() {
      let folderName = 'My presentations'

      if (this.destinatedFolderId) {
        folderName = this.folders
          .find((folder) => folder.folderId === this.destinatedFolderId).folderName
      }

      if (this.selectedPresentationIds && this.selectedPresentationIds.length === 1) {
        Utils.showToastSuccess(`"${Utils.sanitize(this.selectedPresentations[0].name)}" ${this.$i18nPresenter.t('moved to')} "${Utils.sanitize(folderName)}"`)
      } else if (this.selectedPresentationIds && this.selectedPresentationIds.length > 1) {
        Utils.showToastSuccess(`${this.selectedPresentations.length} ${this.$i18nPresenter.t('presentations moved to')} "${Utils.sanitize(folderName)}"`)
      }
      EventBus.$emit('reload-presentations-and-folders')
    },
    checkPermissionFolderItem(permission) {
      if (this.currentFolder && this.currentFolder.ownerId === this.currentUser.id) return true
      if (this.currentFolderPermission &&
        this.currentFolderPermission.includes(permission)) return true
      return false
    },
  },
}
</script>

<style lang="scss" scoped>

.buttons-container {
  @apply flex items-center gap-1 lg:gap-3;

  .__new-presentation-btn--desktop {
    @apply hidden md:inline-flex;
  }

  .__new-presentation-btn--mobile {
    @apply md:hidden inline-flex;
  }

  .__new-folder-button {
    @apply px-2.5 sm:px-4 inline-flex;

    &::v-deep {
      div {
        @apply hidden sm:inline;
      }
    }
  }

  .__import-button {
    @apply px-2.5 sm:px-4;
    &::v-deep {
      div {
        @apply hidden sm:inline-block;
      }
      svg[name="import"] {
        margin-right: 0;
      }
    }
  }
}

</style>
